import { useState } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type Invitasjon } from '../../api/konsoll';

interface Props {
  reiseId: string;
  /** Når invitasjonen sist ble sendt til kontaktpersonen, eller ingenting. */
  sendt: string | null | undefined;
  /** Reisen er trukket tilbake, så det kan ikke sendes en ny lenke. */
  stengt: boolean;
  /** Påminnelsene på e-post er slått av for reisen (T113). */
  paminnelserAv: boolean;
  onSendt: () => void;
}

/**
 * Invitasjonen til kontaktpersonen (T112): når den sist ble sendt, og en knapp for å sende en ny lenke på e-post. Den
 * gamle lenken slutter å virke, så konsulenten bekrefter først. Den nye lenken vises også, så den kan kopieres.
 */
export default function InvitasjonKnapp({ reiseId, sendt, stengt, paminnelserAv, onSendt }: Props) {
  const { t, i18n } = useTranslation();
  const [bekreft, setBekreft] = useState(false);
  const [opptatt, setOpptatt] = useState(false);
  const [resultat, setResultat] = useState<Invitasjon | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  async function settPaminnelser(pa: boolean) {
    setFeil(null);
    try {
      await konsollApi.settPaminnelser(reiseId, !pa);
      onSendt();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    }
  }

  async function send() {
    setOpptatt(true);
    setFeil(null);
    try {
      setResultat(await konsollApi.sendInvitasjon(reiseId));
      setBekreft(false);
      onSendt();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    } finally {
      setOpptatt(false);
    }
  }

  return (
    <div className="mt-2 text-sm text-slate-700">
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>
          {sendt
            ? t('konsoll.invitasjon.sendt', { tid: new Date(sendt).toLocaleString(i18n.resolvedLanguage) })
            : t('konsoll.invitasjon.ikkeSendt')}
        </span>
        {!stengt && !bekreft && (
          <button type="button" onClick={() => setBekreft(true)} className="knapp-stille inline-flex min-h-0 items-center gap-1.5 px-2 py-1 text-sm text-aksent-700">
            <Mail aria-hidden="true" className="h-4 w-4" />
            {t('konsoll.invitasjon.sendNy')}
          </button>
        )}
      </p>
      {!stengt && (
        <label className="mt-1 flex items-center gap-2">
          <input
            type="checkbox"
            checked={!paminnelserAv}
            onChange={(e) => settPaminnelser(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-aksent-600 focus:ring-aksent-600"
          />
          {t('konsoll.invitasjon.paminnelser')}
        </label>
      )}
      {bekreft && (
        <div role="group" aria-label={t('konsoll.invitasjon.sendNy')} className="mt-2 melding-advarsel">
          <p className="mb-2">{t('konsoll.invitasjon.advarsel')}</p>
          <div className="flex gap-2">
            <button type="button" onClick={send} disabled={opptatt} className="knapp-primaer min-h-[2.25rem] px-3 text-sm">
              {t('konsoll.invitasjon.send')}
            </button>
            <button type="button" onClick={() => setBekreft(false)} disabled={opptatt} className="knapp-sekundaer min-h-[2.25rem] px-3 text-sm">
              {t('konsoll.invitasjon.avbryt')}
            </button>
          </div>
        </div>
      )}
      {resultat && (
        <div role="status" className="mt-2 melding-ok">
          <p className="mb-1">{t('konsoll.invitasjon.sendtTil', { epost: resultat.til })}</p>
          <input readOnly aria-label={t('konsoll.ny.lenke')} value={resultat.lenke} onFocus={(e) => e.target.select()} className="felt w-full font-mono text-sm" />
        </div>
      )}
      {feil && (
        <p role="alert" className="mt-2 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
    </div>
  );
}
